import React from 'react';
import Linkify from 'linkify-react';

import { useId } from 'ui/utils';

import Checkbox from './checkbox/Checkbox';
import Input from './input/Input';
import Multiselect from './multiselect/Multiselect';
import Radio from './radio/Radio';
import RadioPanel from './radioPanel/RadioPanel';
import RichText from './richtext/RichText';
import Select from './select/Select';
import Textarea from './textarea/Textarea';

type FieldProps = {
  label?: React.ReactNode;
  error?: string;
  showError?: boolean;
  cornerHint?: React.ReactNode;
  helpText?: React.ReactNode;
  id?: string;
  htmlFor?: string;
  children: React.ReactNode;
};

export function Field({ label, cornerHint, id, htmlFor, error, helpText, children, showError = true }: FieldProps) {
  return (
    <div>
      <div className="flex justify-between">
        {label ? (
          <label htmlFor={htmlFor ?? id} className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        ) : (
          <span />
        )}
        <span className="text-sm text-gray-500">{cornerHint}</span>
      </div>

      <div className="mt-1">{children}</div>

      {error && showError && (
        <p className="mt-2 text-sm text-red-600">
          <Linkify tagName="span" options={{ className: 'text-red-700 hover:text-red-800', target: '_blank' }}>
            {error}
          </Linkify>
        </p>
      )}

      {helpText && !error && (
        <p className="mt-2 text-sm text-gray-500">
          <Linkify tagName="span" options={{ className: 'text-indigo-600 hover:text-indigo-800', target: '_blank' }}>
            {helpText}
          </Linkify>
        </p>
      )}
    </div>
  );
}

export function makeFormField<T>(Component: React.FC<T>) {
  return function ({ id, name, htmlFor, ...rest }: Omit<React.ComponentProps<typeof Field> & T, 'children'>) {
    const internalId = useId();

    if (!id) {
      id = `form-control-${internalId}`;
    }

    if (!name) {
      name = id;
    }

    if (!htmlFor) {
      htmlFor = id;
    }

    const newProps = { id, name, htmlFor, ...rest };

    return (
      <Field {...(newProps as React.ComponentProps<typeof Field>)}>
        <Component {...(newProps as T)} />
      </Field>
    );
  };
}

export const FormInput = makeFormField(Input);
export const FormTextarea = makeFormField(Textarea);
export const FormRichText = makeFormField(RichText);
export const FormSelect = makeFormField(Select);
export const FormMultiselect = Multiselect;
export const FormCheckbox = Checkbox;
export const FormRadio = Radio;
export const FormRadioPanel = RadioPanel;

export default {
  Field,
  FormInput,
  FormCheckbox,
  FormSelect,
  FormMultiselect,
  FormTextarea,
  FormRichText,
  FormRadio,
  FormRadioPanel,
};
